import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI, courierAPI, offersAPI, paymentGatewaysAPI, productsAPI, posAPI, getApiErrorMessage } from '../lib/api';
import { getImageUrl } from '../lib/utils';
import { toast } from 'sonner';
import { CreditCard, Truck, Shield, ChevronLeft, Smartphone, Banknote, Building, MapPin, CheckCircle, AlertCircle, Clock, Tag, X, FileText, Printer } from 'lucide-react';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, clearCart, getItemPrice } = useCart();
  const { user, isWholesale, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);

  // State for fresh product data
  const [verifiedItems, setVerifiedItems] = useState([]);
  const [verifyingCart, setVerifyingCart] = useState(true);

  // ... (existing states)
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [pincodeVerification, setPincodeVerification] = useState({
    checking: false,
    result: null,
    error: null
  });

  // Coupon State
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  // Quotation State
  const [showQuotationDialog, setShowQuotationDialog] = useState(false);
  const [quotationLoading, setQuotationLoading] = useState(false);
  const [createdQuotation, setCreatedQuotation] = useState(null);

  const [address, setAddress] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    pincode: '',
  });

  // Payment Gateways State
  const [availableGateways, setAvailableGateways] = useState([]);
  const pincodeRequestRef = useRef(0);

  useEffect(() => {
    fetchGateways();
  }, []);

  // Fetch fresh product details to ensure latest price and packing charges
  useEffect(() => {
    const refreshCartItems = async () => {
      if (items.length === 0) {
        setVerifiedItems([]);
        setVerifyingCart(false);
        return;
      }

      try {
        const promises = items.map(async (cartItem) => {
          try {
            const res = await productsAPI.getOne(cartItem.product.id);
            // Combine fresh product data with cart quantity
            return {
              ...cartItem,
              product: res.data // Use fresh product data
            };
          } catch (e) {
            console.error(`Failed to refresh product ${cartItem.product.name}`, e);
            return cartItem; // Fallback to existing if fetch fails
          }
        });

        const refreshed = await Promise.all(promises);
        setVerifiedItems(refreshed);
      } catch (error) {
        console.error("Error verifying cart", error);
        setVerifiedItems(items); // Fallback
      } finally {
        setVerifyingCart(false);
      }
    };

    refreshCartItems();
  }, [items]); // Re-run if cart items change

  const fetchGateways = async () => {
    try {
      const response = await paymentGatewaysAPI.getPublic();
      setAvailableGateways(response.data || []);
    } catch (error) {
      console.error('Failed to fetch payment gateways:', error);
    }
  };

  // Update address when user data loads
  useEffect(() => {
    if (user) {
      setAddress(prev => {
        const newAddress = {
          ...prev,
          name: user.name || prev.name,
          phone: user.phone || prev.phone,
        };

        // Try to get address from profile (either single address or first of addresses list)
        const savedAddress = user.address || (user.addresses && user.addresses.length > 0 ? user.addresses[0] : null);

        if (savedAddress) {
          newAddress.line1 = savedAddress.line1 || prev.line1;
          newAddress.line2 = savedAddress.line2 || prev.line2;
          newAddress.city = savedAddress.city || prev.city;
          newAddress.state = savedAddress.state || prev.state;
          newAddress.pincode = savedAddress.pincode || prev.pincode;
        }

        return newAddress;
      });
    }
  }, [user]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login?redirect=/checkout');
    }
  }, [authLoading, user, navigate]);

  if (!authLoading && !user) {
    return null;
  }

  // Show loading while checking authentication OR verifying cart
  if (authLoading || verifyingCart) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">{verifyingCart ? 'Updating prices...' : 'Loading...'}</p>
      </div>
    );
  }

  // Calculate subtotal from VERIFIED items with role-based pricing
  const subtotal = verifiedItems.reduce((total, item) => {
    return total + (getItemPrice(item.product) * item.quantity);
  }, 0);

  // GST is now Inclusive (Back-calculate for display)
  // Assuming 18% standard if mixed, or we could sum up individual item GSTs if we had them.
  // For approximation in frontend display:
  const gstAmount = subtotal - (subtotal / 1.18);

  // Calculate Packing Charges from VERIFIED items (exclude for wholesale and POS users)
  const totalPackingCharges = (user?.role === 'pos_user' || isWholesale) ? 0 : verifiedItems.reduce((acc, item) => {
    return acc + ((item.product.packing_charges || 0) * item.quantity);
  }, 0);

  // Delivery fee from pincode verification only (no default)
  const deliveryFee = pincodeVerification.result?.delivery_charge || 0;

  // Calculate discount
  let discountAmount = 0;
  if (appliedCoupon) {
    if (appliedCoupon.discount_type === 'percentage') {
      discountAmount = (subtotal * appliedCoupon.discount_value) / 100;
      if (appliedCoupon.max_discount && discountAmount > appliedCoupon.max_discount) {
        discountAmount = appliedCoupon.max_discount;
      }
    } else {
      discountAmount = appliedCoupon.discount_value;
    }
  }

  // Total = Subtotal (Inclusive) + Packing + Delivery - Discount
  // Note: GST is already inside subtotal, so we don't add it again.
  const total = Math.max(0, subtotal + totalPackingCharges + deliveryFee - discountAmount);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setCouponLoading(true);
    try {
      const response = await offersAPI.getAll();
      const offers = response.data || [];
      const coupon = offers.find(o => o.coupon_code === couponCode.trim() && o.is_active);

      if (!coupon) {
        toast.error('Invalid coupon code');
        setAppliedCoupon(null);
        return;
      }

      // Validate requirements
      if (subtotal < coupon.min_order_value) {
        toast.error(`Minimum order value of ₹${coupon.min_order_value} required`);
        setAppliedCoupon(null);
        return;
      }

      if (coupon.end_date && new Date(coupon.end_date) < new Date()) {
        toast.error('Coupon has expired');
        setAppliedCoupon(null);
        return;
      }

      setAppliedCoupon(coupon);
      toast.success('Coupon applied successfully!');
    } catch (error) {
      console.error('Failed to validate coupon:', error);
      toast.error('Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  const handleAddressChange = (field, value) => {
    // Prevent redundant updates/checks if value hasn't changed
    if (field === 'pincode' && value === address.pincode) return;

     setAddress(prev => ({ ...prev, [field]: value }));

    // Auto-verify pincode when user enters 6 digits
    if (field === 'pincode') {
      if (value.length === 6 && /^\d{6}$/.test(value)) {
        // Only verify if we haven't already verified this exact pincode
        if (pincodeVerification.result?.pincode !== value) {
          verifyPincode(value);
        }
      } else {
        // Reset verification when pincode is incomplete
        setPincodeVerification({ checking: false, result: null, error: null });
      }
    }
  };

  const verifyPincode = async (pincode) => {
    const requestId = ++pincodeRequestRef.current;
    setPincodeVerification({ checking: true, result: null, error: null });

    try {
      const response = await courierAPI.checkPincode(pincode);
      const result = response.data;

      if (requestId !== pincodeRequestRef.current) return;
      setPincodeVerification({
        checking: false,
        result: result,
        error: null
      });

      if (result.serviceable) {
        toast.success(`✅ Delivery available to ${result.city}, ${result.state}`);

        // Auto-fill city and state if empty or if we want to overwrite
        setAddress(prev => ({
          ...prev,
          city: result.city || prev.city,
          state: result.state || prev.state
        }));

        // If COD was selected but is not available, switch to disabled state or notify
        if (paymentMethod === 'cod' && !result.cod) {
          toast.warning('COD is not available for this location. Please try online payment.');
        }
      } else {
        toast.error(`❌ Sorry, we don't deliver to pincode ${pincode}`);
      }
    } catch (error) {
      console.error('Pincode verification error:', error);
      if (requestId !== pincodeRequestRef.current) return;
      setPincodeVerification({
        checking: false,
        result: null,
        error: 'Failed to verify pincode'
      });
      toast.error('Failed to verify pincode. Please try again.');
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleCreateQuotation = async () => {
    if (!address.name || !address.phone) {
      toast.error('Please provide name and phone number');
      return;
    }

    // Validate minimum order value for wholesale users
    if (subtotal < 4000) {
      toast.error('Minimum order value of ₹4,000 is required for wholesale quotations');
      return;
    }

    setQuotationLoading(true);
    try {
      const orderData = {
        items: verifiedItems.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity
        })),
        shipping_address: address,
        customer_phone: address.phone,
        apply_gst: true,
        // Server applies the coupon itself; customers may not send a discount amount
        coupon_code: appliedCoupon?.coupon_code || undefined,
      };

      const response = await posAPI.createWholesaleQuotation(orderData);
      setCreatedQuotation(response.data);
      setShowQuotationDialog(true);
      toast.success(`Quotation created! #${response.data.order_number}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create quotation'));
    } finally {
      setQuotationLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!address.name || !address.phone || !address.line1 || !address.city || !address.pincode) {
      toast.error('Please fill all required address fields');
      return;
    }

    // Check if pincode is verified and serviceable
    if (!/^\d{6}$/.test(address.pincode)) {
      toast.error('Please enter a valid six-digit pincode');
      return;
    }
    if (address.pincode.length === 6) {
      if (pincodeVerification.result === null) {
        toast.error('Please wait for pincode verification to complete');
        return;
      }

      if (pincodeVerification.result && !pincodeVerification.result.serviceable) {
        toast.error('Cannot place order. Delivery not available to this pincode.');
        return;
      }
    }

    // Validate COD availability if COD is selected
    if (paymentMethod === 'cod' && pincodeVerification.result && !pincodeVerification.result.cod) {
      toast.error('Cash on Delivery not available for this pincode. Please select online payment.');
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        items: items.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
        })),
        shipping_address: address,
        // The backend only needs cod/online; the chosen gateway is sent to /payments/initiate below
        payment_method: paymentMethod === 'cod' ? 'cod' : 'online',
        coupon_code: appliedCoupon?.coupon_code || undefined,
      };

      const response = await ordersAPI.create(orderData);


      if (paymentMethod !== 'cod') {
        // Initiate Online Payment
        try {
          toast.loading('Initiating secure payment...');
          const paymentRes = await paymentGatewaysAPI.initiate({
            order_id: response.data.id,
            gateway_id: paymentOptions.find(option => option.id === paymentMethod)?.db_id || paymentMethod
          });

          const payData = paymentRes.data;

          // Razorpay Flow
          if (payData.provider === 'razorpay') {
            const res = await loadRazorpayScript();
            if (!res) {
              toast.error('Razorpay SDK failed to load. Are you online?');
              toast.dismiss();
              return;
            }

            const options = {
              key: payData.key_id,
              amount: payData.amount,
              currency: payData.currency,
              name: payData.name,
              description: payData.description,
              order_id: payData.order_id,
              prefill: payData.prefill,
              theme: {
                color: "#3399cc"
              },
              handler: async function (responseRp) {
                toast.loading("Verifying Payment...");
                try {
                  await paymentGatewaysAPI.verifyRazorpay({
                    razorpay_payment_id: responseRp.razorpay_payment_id,
                    razorpay_order_id: responseRp.razorpay_order_id,
                    razorpay_signature: responseRp.razorpay_signature,
                    internal_order_id: response.data.id
                  });
                  toast.dismiss();
                   toast.success("Payment Successful!");
                   clearCart();
                   navigate(`/orders/${response.data.id}`);
                } catch (err) {
                  console.error(err);
                  toast.dismiss();
                  toast.error("Payment Verification Failed");
                }
              },
              modal: {
                ondismiss: function () {
                  toast.dismiss();
                  toast.info("Payment Cancelled");
                  navigate(`/orders/${response.data.id}`);
                }
              }
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
            toast.dismiss();
            return;
          }

          // Mock / URL Flow (Legacy)
          if (payData.payment_url) {
            const url = payData.payment_url;
            if (url.includes('mock-success')) {
              // Simulate payment processing
              setTimeout(async () => {
                await paymentGatewaysAPI.confirmMock({ order_id: response.data.id });
                 clearCart();
                toast.dismiss();
                toast.success('Payment Successful!');
                navigate(`/orders/${response.data.id}`);
              }, 1500);
              return; // Stop here, waiting for timeout
            } else {
              // Real redirection
              window.location.href = url;
              return;
            }
          }

          // If no handled flow
          toast.dismiss();
          toast.info("Order placed. Complete payment details sent to email.");

        } catch (payError) {
          console.error("Payment initiation failed", payError);
          toast.dismiss();
          toast.error("Payment initialization failed. You can retry from Order History.");
          // Still navigate to order page (it will be pending payment)
        }
      } else {
        clearCart();
        toast.success('Order placed successfully!');
      }
      navigate(`/orders/${response.data.id}`);
    } catch (error) {
      console.error('Order creation error:', error);
      toast.error(getApiErrorMessage(error, 'Failed to place order'));
    } finally {
      if (paymentMethod === 'cod') setLoading(false);
      // For online payment we might keep loading until modal opens, but safe to setFalse if we handle flow
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Your cart is empty</h1>
        <Button onClick={() => navigate('/products')} className="btn-primary">
          Continue Shopping
        </Button>
      </div>
    );
  }

  // Dynamic Payment Options
  const paymentOptions = [
    { id: 'cod', name: 'Cash on Delivery', icon: Banknote, description: 'Pay when you receive' },
    ...availableGateways.map(g => ({
       id: String(g.id),
      name: g.name,
      icon: Smartphone,
      description: g.is_test_mode ? 'Test Mode' : 'Online Payment',
      merchant_id: g.merchant_id, // Store extra data if needed
      db_id: g.id
    }))
  ];

  return (
    <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 py-8 overflow-x-hidden" data-testid="checkout-page">
      <button
        onClick={() => navigate('/products')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Continue Shopping
      </button>

      <h1 className="text-2xl md:text-3xl font-bold mb-8">Checkout</h1>

      <form onSubmit={handleSubmit}>
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-8">
          {/* Left Column - Forms */}
          <div className="w-full lg:col-span-2 space-y-6 min-w-0">
            {/* Shipping Address */}
            <Card className="w-full max-w-full overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 flex-shrink-0" />
                  Shipping Address
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                {/* Saved Addresses Selector */}
                {user?.addresses && user.addresses.length > 0 && (
                  <div className="mb-2">
                    <Label className="text-xs text-muted-foreground mb-1 block">Quick Fill from Saved Addresses</Label>
                    <select
                      className="w-full p-2 border rounded-md text-sm bg-background max-w-full"
                      onChange={(e) => {
                        const idx = e.target.value;
                        if (idx !== "") {
                          const addr = user.addresses[idx];
                          setAddress(prev => ({
                            ...prev,
                            name: user.name || prev.name,
                            phone: user.phone || prev.phone,
                            line1: addr.line1 || '',
                            line2: addr.line2 || '',
                            city: addr.city || '',
                            state: addr.state || '',
                            pincode: addr.pincode || ''
                          }));
                          // Trigger pincode verify if valid
                          if (addr.pincode && addr.pincode.length === 6) verifyPincode(addr.pincode);
                        }
                      }}
                      defaultValue=""
                    >
                      <option value="" disabled>Select a saved address...</option>
                      {user.addresses.map((addr, idx) => (
                        <option key={idx} value={idx}>
                          {addr.line1}, {addr.city} - {addr.pincode}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2 min-w-0">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={address.name}
                      onChange={(e) => handleAddressChange('name', e.target.value)}
                      placeholder="Enter full name"
                      required
                      data-testid="checkout-name"
                    />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      value={address.phone}
                      onChange={(e) => handleAddressChange('phone', e.target.value)}
                      placeholder="+91 9876543210"
                      required
                      data-testid="checkout-phone"
                    />
                  </div>
                </div>
                <div className="space-y-2 min-w-0">
                  <Label htmlFor="line1">Address Line 1 *</Label>
                  <Input
                    id="line1"
                    value={address.line1}
                    onChange={(e) => handleAddressChange('line1', e.target.value)}
                    placeholder="House/Flat No., Building Name"
                    required
                    data-testid="checkout-address1"
                  />
                </div>
                <div className="space-y-2 min-w-0">
                  <Label htmlFor="line2">Address Line 2</Label>
                  <Input
                    id="line2"
                    value={address.line2}
                    onChange={(e) => handleAddressChange('line2', e.target.value)}
                    placeholder="Street, Area, Landmark"
                  />
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2 min-w-0">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={address.city}
                      onChange={(e) => handleAddressChange('city', e.target.value)}
                      placeholder="City"
                      required
                      data-testid="checkout-city"
                    />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={address.state}
                      onChange={(e) => handleAddressChange('state', e.target.value)}
                      placeholder="State"
                      required
                    />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <Label htmlFor="pincode">Pincode *</Label>
                    <div className="relative">
                      <Input
                        id="pincode"
                        value={address.pincode}
                        onChange={(e) => handleAddressChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit PIN"
                        required
                        data-testid="checkout-pincode"
                        className={`pr-10 ${pincodeVerification.result?.serviceable === false ? 'border-red-500' :
                          pincodeVerification.result?.serviceable === true ? 'border-green-500' : ''
                          }`}
                      />
                      {pincodeVerification.checking && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      {pincodeVerification.result && !pincodeVerification.checking && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {pincodeVerification.result.serviceable ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                      )}
                    </div>

                    {pincodeVerification.result && (
                      <div className={`p-3 rounded-lg text-sm ${pincodeVerification.result.serviceable
                        ? 'bg-green-500/10 border border-green-500/20 text-green-700 dark:text-green-300'
                        : 'bg-red-500/10 border border-red-500/20 text-red-700 dark:text-red-300'
                        }`}>
                        {pincodeVerification.result.serviceable ? (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            <span className="font-medium">Delivery Available</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            <span className="font-medium">Delivery Not Available</span>
                          </div>
                        )}
                      </div>
                    )}

                    {pincodeVerification.error && (
                      <div className="p-3 rounded-lg text-sm bg-yellow-500/10 border border-yellow-500/20 text-yellow-700 dark:text-yellow-300">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          <span>Unable to verify pincode. Please contact support.</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Info message for POS and Wholesale users */}
            {(user?.role === 'pos_user' || isWholesale) && (
              <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 w-full max-w-full overflow-hidden">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                        {user?.role === 'pos_user' ? 'POS User' : 'Wholesale Customer'}
                      </h3>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        You can create a quotation for this order. The quotation will be saved and can be viewed in your orders section.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment Method - Hide for POS and Wholesale users */}
            {user?.role !== 'pos_user' && !isWholesale && (
              <Card className="w-full max-w-full overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 flex-shrink-0" />
                    Payment Method
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="grid sm:grid-cols-2 gap-3">
                    {paymentOptions.map((option) => {
                      const isCodDisabled = option.id === 'cod' && pincodeVerification.result && !pincodeVerification.result.cod;

                      return (
                        <label
                          key={option.id}
                          className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-all 
                          ${paymentMethod === option.id ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/50'}
                          ${isCodDisabled ? 'opacity-50 cursor-not-allowed bg-muted' : ''}
                        `}
                        >
                          <RadioGroupItem
                            value={option.id}
                            id={option.id}
                            disabled={isCodDisabled}
                          />
                          <option.icon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium text-sm whitespace-nowrap">{option.name}</p>
                              {isCodDisabled && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded whitespace-nowrap">Not Available</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground break-words">{option.description}</p>
                          </div>
                        </label>
                      );
                    })}
                  </RadioGroup>
                  {paymentMethod !== 'cod' && (
                    <div className="mt-4">
                      {availableGateways.find(g => g.name.toLowerCase() === paymentMethod)?.is_test_mode && (
                        <p className="text-sm text-amber-600 p-3 bg-amber-50 rounded-lg mb-2">
                          Test Mode Active: No real money will be deducted.
                        </p>
                      )}
                      <p className="text-sm text-blue-600 p-3 bg-blue-50 rounded-lg">
                        You will be redirected to complete your payment securely.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Order Summary */}
          <div className="w-full min-w-0">
            <Card className="sticky top-24 w-full max-w-full overflow-hidden">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Items List - Using Verified Items for accurate pricing */}
                <div className="space-y-4 max-h-60 overflow-y-auto scrollbar-invisible">
                  {(verifiedItems.length > 0 ? verifiedItems : items).map((item, index) => {
                    const product = item.product;

                    // Determine price based on user role
                    let price = product.selling_price;
                    let showDiscount = false;

                    if (user?.role === 'pos_user') {
                      // POS users use POS price
                      price = product.pos_price || product.selling_price;
                    } else if (isWholesale && item.quantity >= (product.wholesale_min_qty || 10)) {
                      // Wholesale users use wholesale price if quantity qualifies
                      price = product.wholesale_price || product.selling_price;
                      showDiscount = product.wholesale_price && product.wholesale_price < product.selling_price;
                    }
                    // Admin and normal customers use selling price (default)

                    const originalPrice = product.selling_price;

                    return (
                      <div key={index} className="flex gap-3">
                        <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0 border">
                          {product.images && product.images[0] ? (
                            <img
                              src={getImageUrl(product.images[0])}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-300">
                              <Tag className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium break-words line-clamp-2">{product.name}</p>
                          <div className="flex flex-wrap justify-between items-center mt-1 gap-2">
                            <div>
                              <p className="text-xs text-muted-foreground whitespace-nowrap">₹{price.toLocaleString()} × {item.quantity}</p>
                            </div>
                            <div className="text-right">
                              {showDiscount && (
                                <p className="text-xs text-muted-foreground line-through whitespace-nowrap">
                                  ₹{(originalPrice * item.quantity).toLocaleString()}
                                </p>
                              )}
                              <p className="text-sm font-semibold whitespace-nowrap">
                                ₹{(price * item.quantity).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          {isWholesale && item.quantity < (product.wholesale_min_qty || 10) && (
                            <p className="text-[10px] text-orange-600 mt-1">
                              Add {Math.max(0, (product.wholesale_min_qty || 10) - item.quantity)} more for wholesale price
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Separator />

                {/* Totals */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>₹{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">GST (Included)</span>
                    <span className="text-slate-500">₹{gstAmount.toFixed(2)}</span>
                  </div>

                  {totalPackingCharges > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Packing Charges</span>
                      <span>₹{totalPackingCharges.toLocaleString()}</span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Delivery Charges</span>
                    <span>₹{deliveryFee.toLocaleString()}</span>
                  </div>

                  {/* Coupon Input */}
                  <div className="pt-2 pb-2">
                    {!appliedCoupon ? (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Coupon Code"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value)}
                          className="h-9"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleApplyCoupon}
                          disabled={couponLoading || !couponCode}
                          className="h-9"
                        >
                          {couponLoading ? '...' : 'Apply'}
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded-md p-2 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-green-700">
                          <Tag className="w-4 h-4" />
                          <span className="font-medium text-xs">Code {appliedCoupon.coupon_code} applied</span>
                        </div>
                        <button onClick={removeCoupon} className="text-green-700 hover:text-green-900">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {appliedCoupon && (
                    <div className="flex justify-between text-green-600 font-medium">
                      <span>Discount</span>
                      <span>-₹{Math.round(discountAmount).toLocaleString()}</span>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="price-tag text-primary">₹{total.toLocaleString()}</span>
                </div>

                {isWholesale && (
                  <p className="text-xs text-primary font-medium p-2 bg-primary/10 rounded">
                    Wholesale prices applied!
                  </p>
                )}

                {/* Create Quotation Button - Only for POS and Wholesale users */}
                {(user?.role === 'pos_user' || isWholesale) && (
                  <>
                    {subtotal < 4000 && (
                      <p className="text-xs text-amber-600 font-medium p-2 bg-amber-600/10 rounded">
                        ⚠️ Minimum order value of ₹4,000 required for quotations
                      </p>
                    )}
                    <Button
                      type="button"
                      onClick={handleCreateQuotation}
                      className="w-full bg-primary hover:bg-primary/90 text-white"
                      disabled={quotationLoading || verifiedItems.length === 0 || subtotal < 4000}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      {quotationLoading ? 'Creating...' : 'Create Quotation'}
                    </Button>
                  </>
                )}

                {/* Hide Place Order button for POS and Wholesale users - they can only create quotations */}
                {user?.role !== 'pos_user' && !isWholesale && (
                  <Button
                    type="submit"
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="place-order-btn"
                  >
                    {loading ? 'Placing Order...' : `Place Order - ₹${total.toLocaleString()}`}
                  </Button>
                )}

                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <Shield className="w-4 h-4" />
                  Secure & Encrypted Payment
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>

      {/* Quotation Success Dialog */}
      <Dialog open={showQuotationDialog} onOpenChange={setShowQuotationDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-emerald-400">Quotation Created Successfully!</span>
            </DialogTitle>
          </DialogHeader>
          {createdQuotation && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-slate-700/50 to-slate-700/30 p-5 rounded-xl border border-slate-600">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Quotation Number</p>
                    <p className="text-2xl font-bold text-white mt-1">{createdQuotation.order_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Total Amount</p>
                    <p className="text-3xl font-bold text-emerald-400 mt-1">₹{createdQuotation.grand_total.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-emerald-300 mb-1">Your quotation has been created!</p>
                    <p className="text-sm text-slate-300">
                      You can view and manage this quotation from your orders page.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => navigate('/orders')}
                  className="flex-1 h-11"
                  variant="outline"
                >
                  View Orders
                </Button>
                <Button
                  onClick={() => {
                    setShowQuotationDialog(false);
                    clearCart();
                    navigate('/');
                  }}
                  className="flex-1 bg-primary hover:bg-primary/90 h-11"
                >
                  Continue Shopping
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
